import {
  UzBadge,
  UzButton,
  UzCard,
  UzConfirmDialog,
  UzEmptyState,
  UzErrorState,
  UzSpinner,
  UzSwitch,
} from '@utazzo/ui';
import { Copy, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { SOURCE_LABEL, formatDateTime } from '../labels';
import {
  lpsErrorText,
  useDeleteLpsConnection,
  useLpsConnections,
  useLpsConnectors,
  useRotateLpsSecret,
  useUpdateLpsConnection,
} from '../use-lps';

import { ConnectionDialog } from './ConnectionDialog';

import type { LpsConnection } from '../lps.api';

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Webhook URL copied');
  } catch {
    toast.error('Could not copy - select the URL and copy it manually');
  }
};

export const LpsConnectionsTab = ({ canWrite }: { canWrite: boolean }): React.JSX.Element => {
  const connections = useLpsConnections();
  const connectors = useLpsConnectors();
  const update = useUpdateLpsConnection();
  const rotate = useRotateLpsSecret();
  const remove = useDeleteLpsConnection();

  const [dialog, setDialog] = useState<{ open: boolean; connection: LpsConnection | null }>({
    open: false,
    connection: null,
  });
  const [confirm, setConfirm] = useState<{
    kind: 'rotate' | 'delete';
    connection: LpsConnection;
  } | null>(null);

  if (connections.isLoading || connectors.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <UzSpinner />
      </div>
    );
  }

  const failure = connections.error ?? connectors.error;
  if (failure) {
    return (
      <UzErrorState
        title="Could not load connections"
        description={lpsErrorText(failure, 'Try again in a moment.')}
        onRetry={() => {
          void connections.refetch();
          void connectors.refetch();
        }}
      />
    );
  }

  const list = connections.data ?? [];
  const catalogue = connectors.data ?? [];
  const labelOf = (id: string) => catalogue.find((c) => c.id === id)?.label ?? id;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-zinc-600">
          Each connection has its own webhook URL. Paste it into the form, Meta app or WhatsApp
          provider and leads arrive in the Inbound leads tab.
        </p>
        {canWrite && (
          <UzButton
            leftIcon={<Plus className="size-4" />}
            onClick={() => setDialog({ open: true, connection: null })}
          >
            New connection
          </UzButton>
        )}
      </div>

      {list.length === 0 ? (
        <UzEmptyState
          title="No connections yet"
          description="Connect a website form, a Meta lead form, a WhatsApp number or a landing page to start receiving leads."
          action={
            canWrite ? (
              <UzButton
                leftIcon={<Plus className="size-4" />}
                onClick={() => setDialog({ open: true, connection: null })}
              >
                New connection
              </UzButton>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {list.map((c) => {
            const connector = catalogue.find((k) => k.id === c.connectorId);
            return (
              <UzCard
                key={c.id}
                title={
                  <span className="flex items-center gap-2">
                    {c.name}
                    <UzBadge tone={c.enabled ? 'success' : 'outline'}>
                      {c.enabled ? 'Live' : 'Paused'}
                    </UzBadge>
                  </span>
                }
                description={
                  connector && SOURCE_LABEL[connector.source] !== labelOf(c.connectorId)
                    ? `${labelOf(c.connectorId)} · ${SOURCE_LABEL[connector.source]}`
                    : labelOf(c.connectorId)
                }
                actions={
                  canWrite ? (
                    <div className="flex items-center gap-1">
                      <UzButton
                        size="sm"
                        variant="ghost"
                        tooltip="Edit"
                        aria-label={`Edit ${c.name}`}
                        onClick={() => setDialog({ open: true, connection: c })}
                      >
                        <Pencil className="size-4" />
                      </UzButton>
                      <UzButton
                        size="sm"
                        variant="ghost"
                        tooltip="New webhook URL"
                        aria-label={`Rotate secret for ${c.name}`}
                        onClick={() => setConfirm({ kind: 'rotate', connection: c })}
                      >
                        <KeyRound className="size-4" />
                      </UzButton>
                      <UzButton
                        size="sm"
                        variant="ghost"
                        tooltip="Delete"
                        aria-label={`Delete ${c.name}`}
                        onClick={() => setConfirm({ kind: 'delete', connection: c })}
                      >
                        <Trash2 className="size-4" />
                      </UzButton>
                    </div>
                  ) : undefined
                }
              >
                <div className="space-y-4">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                    <div>
                      <dt className="text-xs text-zinc-500">Leads</dt>
                      <dd className="font-semibold tabular-nums">{c.leadCount}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-zinc-500">Last lead</dt>
                      <dd className="text-xs">{formatDateTime(c.lastEventAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-zinc-500">CRM source</dt>
                      <dd className="font-mono text-xs">{c.crmSourceCode ?? 'default'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-zinc-500">Sends to CRM</dt>
                      <dd className="text-xs">{c.forwardToCrm ? 'Yes' : 'No'}</dd>
                    </div>
                  </dl>

                  {c.connectorId === 'utazzo-crm' ? (
                    <p className="text-xs text-zinc-500">
                      Fed in process by the CRM lead form: every lead created there shows up here,
                      assigned to its owner or sent through round-robin when left unowned. There is
                      no webhook to paste anywhere.
                    </p>
                  ) : (
                    <div>
                      <div className="mb-1 text-xs text-zinc-500">Webhook URL</div>
                      <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1.5">
                        <code
                          className="min-w-0 flex-1 truncate font-mono text-[11px] text-zinc-700"
                          title={c.webhookUrl}
                        >
                          {c.webhookUrl}
                        </code>
                        <UzButton
                          size="sm"
                          variant="ghost"
                          aria-label="Copy webhook URL"
                          onClick={() => void copy(c.webhookUrl)}
                        >
                          <Copy className="size-3.5" />
                        </UzButton>
                      </div>
                    </div>
                  )}

                  {canWrite && (
                    <UzSwitch
                      id={`lps-live-${c.id}`}
                      label="Accept leads"
                      description="Paused connections acknowledge deliveries but store nothing."
                      checked={c.enabled}
                      disabled={update.isPending}
                      onCheckedChange={(enabled) => update.mutate({ id: c.id, input: { enabled } })}
                    />
                  )}
                </div>
              </UzCard>
            );
          })}
        </div>
      )}

      <ConnectionDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        connectors={catalogue}
        connection={dialog.connection}
      />

      <UzConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        tone={confirm?.kind === 'delete' ? 'destructive' : 'primary'}
        title={
          confirm?.kind === 'delete'
            ? `Delete ${confirm.connection.name}?`
            : `Issue a new webhook URL for ${confirm?.connection.name}?`
        }
        description={
          confirm?.kind === 'delete'
            ? 'Its webhook stops accepting leads. Leads already received stay in the inbox.'
            : 'The current URL stops working immediately. Paste the new one into the provider right after.'
        }
        confirmLabel={confirm?.kind === 'delete' ? 'Delete connection' : 'Issue new URL'}
        onConfirm={async () => {
          if (!confirm) return;
          if (confirm.kind === 'delete') await remove.mutateAsync(confirm.connection.id);
          else await rotate.mutateAsync(confirm.connection.id);
          setConfirm(null);
        }}
      />
    </div>
  );
};
